import React, { useId, useMemo, useState } from 'react';
import { ChevronDown, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';

export type AssetExposureSetting = {
  enabled: boolean;
  radiusKm: number;
  resourceSetIds?: string[];
};

export const DEFAULT_EXPOSURE_RADIUS_KM = 50;
export const MAX_EXPOSURE_RADIUS_KM = 500;
/** Asset exposure is a paid feature; while locked it stays off and saves as off. */
export const ASSET_EXPOSURE_LOCKED = true;

export function normalizeAssetExposureSetting(
  raw: Partial<AssetExposureSetting> | null | undefined
): AssetExposureSetting {
  const radius = Number(raw?.radiusKm);
  const radiusKm =
    Number.isFinite(radius) && radius > 0
      ? Math.min(radius, MAX_EXPOSURE_RADIUS_KM)
      : DEFAULT_EXPOSURE_RADIUS_KM;
  const ids = Array.isArray(raw?.resourceSetIds)
    ? raw.resourceSetIds.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    : [];
  return {
    enabled: !ASSET_EXPOSURE_LOCKED && raw?.enabled === true,
    radiusKm,
    ...(ids.length > 0 ? { resourceSetIds: ids } : {}),
  };
}

function AssetPicker({
  selectedIds,
  disabled,
  onChange,
}: {
  selectedIds: string[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: resourceSets = [], isLoading } = useResourceSets();
  const located = useMemo(
    () =>
      resourceSets
        .filter((set) => set.geometry)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [resourceSets]
  );
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return located;
    return located.filter((set) =>
      (set.name || '').toLowerCase().includes(term)
    );
  }, [located, search]);
  const selected = new Set(selectedIds);

  const toggle = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    onChange([...next]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="tw:h-7 tw:gap-1 tw:px-2 tw:text-[11px]"
          />
        }
      >
        {selectedIds.length > 0
          ? `${selectedIds.length} assets`
          : 'All located assets'}
        <ChevronDown className="tw:size-3.5 tw:opacity-70" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="tw:w-72 tw:gap-2 tw:p-2"
        sideOffset={6}
      >
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search assets"
          aria-label="Search assets"
          className="tw:h-7 tw:text-xs"
        />
        {selectedIds.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="tw:h-6 tw:justify-start tw:px-1 tw:text-[11px]"
            onClick={() => onChange([])}
          >
            Use all located assets
          </Button>
        ) : null}
        <ul className="tw:m-0 tw:max-h-60 tw:list-none tw:space-y-1 tw:overflow-y-auto tw:p-0">
          {isLoading ? (
            <li className="tw:px-1 tw:text-[11px] tw:text-muted-foreground">
              Loading assets…
            </li>
          ) : visible.length === 0 ? (
            <li className="tw:px-1 tw:text-[11px] tw:text-muted-foreground">
              No assets with a location.
            </li>
          ) : (
            visible.map((set) => (
              <li
                key={set.resource_set_id}
                className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:px-1 tw:py-1 tw:hover:bg-muted/60"
              >
                <Checkbox
                  checked={selected.has(set.resource_set_id)}
                  onCheckedChange={(value) =>
                    toggle(set.resource_set_id, value === true)
                  }
                  aria-label={set.name}
                />
                <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-[11px]">
                  {set.name}
                </span>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Report-level setting: list which org assets sit inside or near each pack's
 * features.
 */
export default function AssetExposureControls({
  value,
  disabled = false,
  onChange,
}: {
  value: AssetExposureSetting;
  disabled?: boolean;
  onChange: (next: AssetExposureSetting) => void;
}) {
  const [radiusText, setRadiusText] = useState(String(value.radiusKm));
  const [prevRadius, setPrevRadius] = useState(value.radiusKm);
  if (prevRadius !== value.radiusKm) {
    setPrevRadius(value.radiusKm);
    setRadiusText(String(value.radiusKm));
  }

  const commitRadius = () => {
    const next = normalizeAssetExposureSetting({
      ...value,
      radiusKm: Number(radiusText),
    });
    setRadiusText(String(next.radiusKm));
    if (next.radiusKm !== value.radiusKm) onChange(next);
  };

  const switchId = useId();
  const locked = ASSET_EXPOSURE_LOCKED;
  const toggle = (
    <div className="tw:flex tw:items-start tw:gap-2.5">
      <Switch
        id={switchId}
        checked={!locked && value.enabled}
        disabled={disabled || locked}
        onCheckedChange={(checked) =>
          onChange({ ...value, enabled: checked === true })
        }
        className="tw:mt-0.5"
      />
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
        <Label
          htmlFor={switchId}
          className={cn(
            'tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:leading-5',
            locked && 'tw:text-muted-foreground'
          )}
        >
          Include asset exposure
          {locked ? <Lock className="tw:size-3.5" aria-label="Locked" /> : null}
        </Label>
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          See which of your assets are near these features.
        </p>
      </div>
    </div>
  );

  return (
    <div className="tw:mb-3 tw:flex tw:flex-col tw:gap-2">
      {locked ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger render={<div className="tw:w-fit">{toggle}</div>} />
            <TooltipContent className="dark">
              Upgrade to unlock asset exposure.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        toggle
      )}
      {!locked && value.enabled ? (
        <div className="tw:ml-11 tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2">
          <label className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs">
            Within
            <Input
              type="number"
              min={1}
              max={MAX_EXPOSURE_RADIUS_KM}
              value={radiusText}
              disabled={disabled}
              onChange={(e) => setRadiusText(e.target.value)}
              onBlur={commitRadius}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRadius();
              }}
              aria-label="Exposure radius in kilometers"
              className="tw:h-7 tw:w-20 tw:text-xs"
            />
            km
          </label>
          <AssetPicker
            selectedIds={value.resourceSetIds ?? []}
            disabled={disabled}
            onChange={(ids) =>
              onChange(
                normalizeAssetExposureSetting({ ...value, resourceSetIds: ids })
              )
            }
          />
        </div>
      ) : null}
    </div>
  );
}
